import { objectWithout } from '@art-suite/art-core-ts';
import { useCallback } from 'react';
import type { Location, NavigateOptions, To } from 'react-router';

import type { NamedFeatureFlags } from '@kaiizen/kai-pro-api';

import { isOptedIntoNewExperience } from '@/Client/Lib/newExperienceLib';
import { writeFocusedTaskExpandToSearchParams } from '@/Client/Partials/FocusedScopeEditor/focusedTaskExpandUrl';
import type { ScanFocusPanelPage } from '@/Client/Partials/FocusPanel';
import { getScanUrl, type ScanUrlParts } from '../../State';

type AdminScanPageNavigationParams = {
  scanId: string;
  scanUrlParts: ScanUrlParts;
  focusPanel?: ScanFocusPanelPage;
  progress: boolean;
  groupId?: string;
  taskId?: string;
  featureFlags: NamedFeatureFlags;
  location: Location;
  /** Guard-aware navigate (useGuardedNavigate) so unsaved-changes guards apply. */
  navigate: (to: To, options?: NavigateOptions) => void;
  navigateToScan: (params: ScanUrlParts & { photoId?: string }) => void;
  navigateToScanFocusPanelPage: (params: {
    scanId: string;
    panel: ScanFocusPanelPage;
    groupId?: string;
    contractorId?: string;
    taskId?: string;
    replace?: boolean;
  }) => void;
  navigateToProject: (params: { projectId: string }) => void;
  projectId?: string;
};

export const useAdminScanPageNavigation = ({
  scanId,
  scanUrlParts,
  focusPanel,
  progress,
  groupId,
  taskId,
  featureFlags,
  location,
  navigate,
  navigateToScan,
  navigateToScanFocusPanelPage,
  navigateToProject,
  projectId,
}: AdminScanPageNavigationParams) => {
  const focusPanelNavigationActive = !!focusPanel;
  // Project Overview describes the whole project and only has a root route.
  // Picking a group or task must enter the editor rather than constructing an
  // invalid scoped overview URL.
  const keepFocusPanelOnScopeNavigation = focusPanelNavigationActive && focusPanel !== 'artifact' && focusPanel !== 'overview';

  const exitLargePhotoViewer = useCallback(
    () => navigateToScan(objectWithout(scanUrlParts, 'photoId')),
    [navigateToScan, scanUrlParts],
  );

  const navigateWithinFocusPanel = useCallback(
    (params: { groupId?: string; contractorId?: string; taskId?: string; expandTask?: boolean; replace?: boolean }) => {
      if (!focusPanel) return;
      if (params.expandTask) {
        const pathname = getScanUrl({
          scanId,
          groupId: params.groupId,
          contractorId: params.contractorId,
          taskId: params.taskId,
          focusPanel,
          newScopeBuilderFlow: isOptedIntoNewExperience(featureFlags),
        });
        const nextSearch = writeFocusedTaskExpandToSearchParams(new URLSearchParams(location.search), true);
        const search = nextSearch.toString();
        navigate({ pathname, search: search ? `?${search}` : '' }, { replace: params.replace });
        return;
      }
      navigateToScanFocusPanelPage({
        scanId,
        panel: focusPanel,
        groupId: params.groupId,
        contractorId: params.contractorId,
        taskId: params.taskId,
        replace: params.replace,
      });
    },
    [featureFlags, focusPanel, location.search, navigate, navigateToScanFocusPanelPage, scanId],
  );

  const selectAllFocusPanelScope = useCallback(
    () => navigateWithinFocusPanel({}),
    [navigateWithinFocusPanel],
  );

  const navigateToFocusPanelPage = useCallback(
    (panel: ScanFocusPanelPage) => {
      navigateToScanFocusPanelPage({
        scanId,
        panel,
        groupId,
        contractorId: scanUrlParts.contractorId,
        taskId,
      });
    },
    [groupId, navigateToScanFocusPanelPage, scanId, scanUrlParts.contractorId, taskId],
  );

  const setCurrentGroupIdAndTaskId = useCallback(
    (nextGroupId: string | undefined, nextTaskId: string | undefined) => {
      navigateToScan({ scanId, groupId: nextGroupId, taskId: nextTaskId, progress });
    },
    [navigateToScan, progress, scanId],
  );

  /**
   * Contractor-bucketed selection. Its own setter rather than a flag on
   * setCurrentGroupIdAndTaskId, because a route carries a group or a contractor,
   * never both — passing one has to clear the other.
   *
   * Same branch setCurrentGroupIdAndTaskId's callers apply externally
   * (selectCloseoutSidebarGroup/Task): jump straight to the final focus-panel
   * URL when one is already open, rather than dropping to the panel-less
   * route and waiting on the redirect effect to add it back. Without this,
   * every contractor-tab click paid for a render of GenericLoadingPage that
   * the equivalent group-tab click never did — visible as an extra reload.
   */
  const setCurrentContractorIdAndTaskId = useCallback(
    (nextContractorId: string | undefined, nextTaskId: string | undefined) => {
      if (keepFocusPanelOnScopeNavigation) {
        navigateWithinFocusPanel({ contractorId: nextContractorId, taskId: nextTaskId });
        return;
      }
      navigateToScan({ scanId, contractorId: nextContractorId, taskId: nextTaskId });
    },
    [keepFocusPanelOnScopeNavigation, navigateToScan, navigateWithinFocusPanel, scanId],
  );

  const onBack = useCallback(
    () => {
      if (focusPanel === 'overview') {
        navigate('/dashboard');
        return;
      }
      if (projectId) navigateToProject({ projectId });
    },
    [focusPanel, navigate, navigateToProject, projectId],
  );

  return {
    focusPanelNavigationActive,
    keepFocusPanelOnScopeNavigation,
    exitLargePhotoViewer,
    navigateWithinFocusPanel,
    selectAllFocusPanelScope,
    navigateToFocusPanelPage,
    setCurrentGroupIdAndTaskId,
    setCurrentContractorIdAndTaskId,
    onBack,
  };
};
